import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ShieldCheck, CheckCircle2, AlertTriangle, Loader2, Lock } from "lucide-react";
import SignaturePad from "@/components/SignaturePad";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Pagina PUBBLICA — /paziente/consenso/:token
 * Nessuna autenticazione. Il paziente arriva da link SMS/email.
 * Step: peek → OTP → firma 3 sezioni + canvas → ricevuta.
 */
export default function PublicConsent() {
  const { token } = useParams();
  const [phase, setPhase] = useState("loading"); // loading | invalid | expired | signed | otp | sign | done
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);

  const [secA, setSecA] = useState(false);
  const [secB, setSecB] = useState(false);
  const [secC, setSecC] = useState(false);
  const [sigDataUrl, setSigDataUrl] = useState(null);

  // Tutore (solo se minorenne)
  const [guardianOn, setGuardianOn] = useState(false);
  const [guardianName, setGuardianName] = useState("");
  const [guardianSurname, setGuardianSurname] = useState("");
  const [guardianRapporto, setGuardianRapporto] = useState("genitore");

  const [receipt, setReceipt] = useState(null);

  useEffect(() => {
    if (!token) { setPhase("invalid"); return; }
    (async () => {
      try {
        const { data } = await axios.get(`${API}/public/consent/info/${encodeURIComponent(token)}`);
        setInfo(data);
        if (data.signed) setPhase("signed");
        else if (data.otp_verified) setPhase("sign");
        else setPhase("otp");
        if (data.minor) setGuardianOn(true);
      } catch (err) {
        const status = err?.response?.status;
        if (status === 404) setPhase("invalid");
        else { setPhase("invalid"); setError(err?.response?.data?.detail || "Errore caricamento"); }
      }
    })();
  }, [token]);

  const submitOtp = async (e) => {
    e?.preventDefault();
    if (!otp.trim()) { toast.error("Inserisci il codice"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/public/consent/verify-otp`, { token, code: otp.trim() });
      toast.success("Codice verificato");
      setPhase("sign");
    } catch (err) {
      const s = err?.response?.status;
      const detail = err?.response?.data?.detail || "Codice non valido";
      if (s === 429) toast.error("Troppi tentativi, richiedi un nuovo link al medico");
      else toast.error(detail);
    } finally { setBusy(false); }
  };

  const submitSign = async (e) => {
    e?.preventDefault();
    if (!(secA && secB && secC)) { toast.error("Accetta tutte e 3 le sezioni"); return; }
    if (!sigDataUrl) { toast.error("Firma obbligatoria"); return; }
    if (guardianOn && (!guardianName.trim() || !guardianSurname.trim())) {
      toast.error("Inserisci i dati del tutore/genitore"); return;
    }
    setBusy(true);
    try {
      const b64 = sigDataUrl.startsWith("data:") ? sigDataUrl.split(",")[1] : sigDataUrl;
      await axios.post(`${API}/public/consent/sign`, {
        token,
        section_privacy: true, section_referto_online: true, section_ia_audio: true,
        signature_png_base64: b64,
      });
      setReceipt({
        signed_at: new Date().toISOString(),
        consent_id: info?.consent_id,
        consent_version: info?.consent_version,
        doctor_name: info?.doctor_name,
        sections: { privacy: true, referto_online: true, ia_audio: true },
        guardian: guardianOn ? {
          name: guardianName, surname: guardianSurname, rapporto: guardianRapporto,
        } : null,
      });
      setPhase("done");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore firma consenso");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-brand-bg py-6 sm:py-10 px-3" data-testid="public-consent-page">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-2 mb-6">
          <ShieldCheck className="text-brand-navy" size={26} />
          <div>
            <div className="font-extrabold text-brand-navy text-lg leading-tight">Prime Vision Software</div>
            <div className="text-xs text-slate-500">Consenso privacy · versione {info?.consent_version || "—"}</div>
          </div>
        </div>

        {phase === "loading" && (
          <div className="bg-white rounded-xl border border-slate-200 p-6 text-center text-slate-500">
            <Loader2 className="animate-spin mx-auto mb-2" size={20} /> Caricamento…
          </div>
        )}

        {phase === "invalid" && (
          <div className="bg-white rounded-xl border border-red-200 p-6 text-center" data-testid="consent-invalid">
            <AlertTriangle className="text-red-500 mx-auto mb-2" size={28} />
            <div className="font-bold text-red-700">Link non valido o scaduto</div>
            <div className="text-sm text-slate-600 mt-1">Contatta il tuo medico per riceverne uno nuovo.</div>
          </div>
        )}

        {phase === "signed" && (
          <div className="bg-white rounded-xl border border-emerald-200 p-6 text-center" data-testid="consent-already-signed">
            <CheckCircle2 className="text-emerald-500 mx-auto mb-2" size={28} />
            <div className="font-bold text-emerald-700">Consenso già firmato</div>
            <div className="text-sm text-slate-600 mt-1">Grazie. Non serve fare altro.</div>
          </div>
        )}

        {phase === "otp" && (
          <form onSubmit={submitOtp} className="bg-white rounded-xl border border-slate-200 p-6 space-y-4" data-testid="consent-otp-form">
            <div className="text-sm text-slate-700">
              Ciao <b>{info?.patient_name} {info?.patient_surname}</b>. Il tuo medico
              {info?.doctor_name ? <> (<b>{info.doctor_name}</b>)</> : null} ti chiede di firmare il consenso privacy.
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded p-3 text-xs text-blue-900 flex items-start gap-2">
              <Lock size={14} className="mt-0.5 shrink-0" />
              <span>Inserisci il codice OTP ricevuto via SMS/email. Il codice è valido 15 minuti.</span>
            </div>
            <input
              data-testid="consent-otp-input" autoFocus inputMode="numeric" maxLength={8}
              value={otp} onChange={(e) => setOtp(e.target.value)}
              className="w-full px-4 py-3 rounded-lg border border-slate-300 bg-white text-center font-mono text-2xl tracking-widest"
              placeholder="123456"
            />
            <button data-testid="consent-otp-submit" disabled={busy}
                    className="w-full bg-brand-navy hover:bg-brand-navyDark text-white font-bold rounded-lg px-6 py-3 disabled:opacity-60 flex items-center justify-center gap-2">
              {busy && <Loader2 size={16} className="animate-spin" />} Verifica codice
            </button>
          </form>
        )}

        {phase === "sign" && (
          <form onSubmit={submitSign} className="bg-white rounded-xl border border-slate-200 p-6 space-y-4" data-testid="consent-sign-form">
            <div className="text-sm text-slate-700">
              Paziente: <b>{info?.patient_name} {info?.patient_surname}</b>
              {info?.minor && <span className="ml-2 bg-amber-100 text-amber-800 text-[10px] font-bold px-2 py-0.5 rounded-full">MINORENNE</span>}
            </div>

            <div className="space-y-3">
              {[
                {key: "A", set: setSecA, val: secA, testid: "consent-sec-privacy",
                 text: "Ho ricevuto e compreso l'informativa privacy e il trattamento dei dati sanitari."},
                {key: "B", set: setSecB, val: secB, testid: "consent-sec-referto",
                 text: "Acconsento all'invio del referto online tramite email e/o SMS con link PDF."},
                {key: "C", set: setSecC, val: secC, testid: "consent-sec-ia",
                 text: "Acconsento all'uso temporaneo di audio/trascrizione per assistenza IA, con conservazione massima 15 giorni."},
              ].map((s) => (
                <label key={s.key} className="flex items-start gap-3 cursor-pointer p-3 rounded-lg border border-slate-200 hover:border-brand-azure">
                  <input type="checkbox" data-testid={s.testid} checked={s.val} onChange={(e) => s.set(e.target.checked)} className="mt-1 w-4 h-4" />
                  <div>
                    <div className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">Sezione {s.key}</div>
                    <div className="text-sm text-slate-800">{s.text}</div>
                  </div>
                </label>
              ))}
            </div>

            {guardianOn && (
              <div className="border border-amber-200 bg-amber-50 rounded-lg p-3 space-y-2" data-testid="consent-guardian-block">
                <div className="text-xs font-bold text-amber-900">Firma di genitore/tutore per paziente minorenne</div>
                <div className="grid grid-cols-2 gap-2">
                  <input value={guardianName} onChange={(e) => setGuardianName(e.target.value)}
                         data-testid="consent-guardian-name" placeholder="Nome tutore"
                         className="px-2 py-1.5 rounded border border-slate-300 text-sm bg-white" />
                  <input value={guardianSurname} onChange={(e) => setGuardianSurname(e.target.value)}
                         data-testid="consent-guardian-surname" placeholder="Cognome tutore"
                         className="px-2 py-1.5 rounded border border-slate-300 text-sm bg-white" />
                </div>
                <select value={guardianRapporto} onChange={(e) => setGuardianRapporto(e.target.value)}
                        data-testid="consent-guardian-rapporto"
                        className="w-full px-2 py-1.5 rounded border border-slate-300 text-sm bg-white">
                  <option value="genitore">Genitore</option>
                  <option value="tutore_legale">Tutore legale</option>
                </select>
                <div className="text-[11px] text-amber-800">Dichiaro di esercitare la responsabilità genitoriale/tutela legale sul paziente e firmo in sua vece.</div>
              </div>
            )}

            <div>
              <div className="text-xs font-bold text-slate-700 mb-1">Firma</div>
              <SignaturePad onChange={setSigDataUrl} dataTestid="consent-signature-canvas" />
            </div>

            <button data-testid="consent-sign-submit" disabled={busy}
                    className="w-full bg-brand-navy hover:bg-brand-navyDark text-white font-bold rounded-lg px-6 py-3 disabled:opacity-60 flex items-center justify-center gap-2">
              {busy && <Loader2 size={16} className="animate-spin" />} Firma e conferma consenso
            </button>
          </form>
        )}

        {phase === "done" && receipt && (
          <div className="bg-white rounded-xl border border-emerald-200 p-6 space-y-3" data-testid="consent-receipt">
            <div className="text-center">
              <CheckCircle2 className="text-emerald-500 mx-auto mb-2" size={32} />
              <div className="font-bold text-emerald-700 text-lg">Consenso acquisito</div>
              <div className="text-xs text-slate-500">{new Date(receipt.signed_at).toLocaleString("it-IT")}</div>
            </div>
            <div className="border-t border-slate-200 pt-3 text-sm space-y-1">
              <div><span className="text-slate-500">ID consenso:</span> <span className="font-mono text-slate-700">{receipt.consent_id}</span></div>
              <div><span className="text-slate-500">Versione:</span> {receipt.consent_version}</div>
              {receipt.doctor_name && <div><span className="text-slate-500">Medico:</span> {receipt.doctor_name}</div>}
              <div><span className="text-slate-500">Sezioni firmate:</span> A · B · C</div>
              {receipt.guardian && <div><span className="text-slate-500">Firmato da tutore:</span> {receipt.guardian.name} {receipt.guardian.surname} ({receipt.guardian.rapporto === "tutore_legale" ? "tutore legale" : "genitore"})</div>}
            </div>
            <div className="text-[11px] text-slate-500 border-t border-slate-100 pt-3">
              Il consenso resta valido fino a revoca. Puoi contattare il tuo medico per esercitare i diritti GDPR.
            </div>
          </div>
        )}

        {phase !== "loading" && phase !== "invalid" && (
          <div className="mt-4 text-[10px] text-slate-400 text-center">
            Prime Vision Software è responsabile del trattamento per conto del medico. Il titolare è il medico/centro che ti ha inviato questo link.
          </div>
        )}
      </div>
    </div>
  );
}
